import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Event, NavigationEnd, Router } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  Observable,
  filter,
  firstValueFrom,
  map,
  of,
  first,
  from,
  take,
  BehaviorSubject,
  Subscription,
} from "rxjs";
import { Features } from "src/app/common/enums/features.enum";
import { PatientListItem } from "src/app/common/interfaces/patient.interface";
import { UserFacility } from "src/app/common/interfaces/user.interface";
import { PatientClient } from "src/app/common/services/patient.client";
import { SecurityFeatureUtility } from "src/app/common/services/security-feature.utility";
import { SecurityGroupClient } from "src/app/common/services/security-group.client";
import { StorageService } from "src/app/common/services/storage.service";
import { UserClient } from "src/app/common/services/user.client";
import { DropdownComponent } from "../../common/components/core/dropdown/dropdown.component";
import { ZpaxLogoComponent } from "../../common/components/core/zpax-logo/zpax-logo.component";
import {
  LinkConfig,
  LinkGroupConfig,
} from "../../common/interfaces/links.interface";
import { UserService } from "../../common/services/user.service";
import { ActionsComponent } from "./actions/actions.component";
import { ClockInComponent } from "./clock-in/clock-in.component";
import { FacilitySelectorComponent } from "./facility-selector/facility-selector.component";
import { LinksComponent } from "./links/links.component";
import { NavigationMenuComponent } from "./navigation-menu/navigation-menu.component";
import { NavigationHelperService } from "../../common/services/navigation-helper.service";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatIconModule } from "@angular/material/icon";
import { AuthService } from "src/app/services/auth.service";
import { DropdownItem } from "src/app/common/interfaces/dropdown-item.interface";
import { CorporationClient } from "src/app/common/services/corporation.client";
import { CorporationSelectorComponent } from "./corporation-selector/corporation-selector.component";
import { FacilitiesClient } from "src/app/common/services/facility.client";
import { MatSelectChange } from "@angular/material/select";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationModalComponent } from "src/app/common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { environment } from "environment";
import { entityKeys } from 'src/app/common/constants/app-constants';

@UntilDestroy()
@Component({
  standalone: true,
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  imports: [
    ZpaxLogoComponent,
    ReactiveFormsModule,
    CommonModule,
    FacilitySelectorComponent,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    MatMenuModule,
    MatIconModule,
    CorporationSelectorComponent,
  ],
})
export class HeaderComponent implements OnInit {
  facilitiesForUser$: BehaviorSubject<UserFacility[]> = new BehaviorSubject<
    UserFacility[]
  >([]);
  user!: any;
  links!: LinkGroupConfig[];
  selectedFacilityId!: string;
  isInAdminRoute!: boolean;
  userDisplayName!: string;
  patient!: PatientListItem;
  defaultFacilityId!: string;
  corporationsForUser$: BehaviorSubject<DropdownItem[] | null> =
    new BehaviorSubject<DropdownItem[] | null>(null);
  corporations: DropdownItem[] = [];
  selectedCorporationId: string | null = null;
  private allowedRoutes = ["home", "patient", "user", "agenda", "time-card","reports","co-sign"];
  isShowCropFacilityDropdown = false;
  corporationsForUserSubscription!: Subscription;
  shouldReloadFacilitiesDropDownSubscription!: Subscription;
  shouldReloadCorporationDropDownSubscription!: Subscription;
  facilities: UserFacility[] = [];
  inReportsView: boolean = false;

  constructor(
    private userService: UserService,
    private storageService: StorageService,
    private router: Router,
    private userClient: UserClient,
    private securityGroupClient: SecurityGroupClient,
    private securityUtility: SecurityFeatureUtility,
    private patientClient: PatientClient,
    private navigationHelperService: NavigationHelperService,
    private authService: AuthService,
    private corporationClient: CorporationClient,
    private matDialog: MatDialog,
    private facilitiesClient: FacilitiesClient
  ) {
    const initialUrl = this.router.url;
    this.isShowCropFacilityDropdown =
      this.showFacilityCropDropdownForRoute(initialUrl);
    this.router.events
      .pipe(
        filter((event: Event) => event instanceof NavigationEnd),
        map((event: Event) => (event as NavigationEnd).url),
        untilDestroyed(this)
      )
      .subscribe((url: string) => {
        this.isShowCropFacilityDropdown =
          this.showFacilityCropDropdownForRoute(url);
      });
  }

  private showFacilityCropDropdownForRoute(url: string): boolean {
    const basePath = url.split('?')[0].split('/')[1];
   
    if (this.allowedRoutes.includes(basePath)) {
      if (basePath === entityKeys.reports) {
        this.inReportsView = true;
      } else {
        this.inReportsView = false;
      }
      return !(basePath === entityKeys.user && url.split('/')[2]);
    }
    return false;
  }

  async ngOnInit() {
    this.storageService.loggedIn
      .pipe(filter((item) => item))
      .subscribe(() => this.initializeComponent());
  }

  initializeComponent() {
    this.user = this.userService.getCurrentUser();
    this.userDisplayName = this.buildUserDisplayName(this.user);
    this.selectedCorporationId = this.storageService.getSelectedCorporationId();
    if (this.user.issuperuser || this.user.isPhysician) {
      this.fetchCorporationsForUser();
      this.corporationsForUserSubscription =
        this.corporationsForUser$.subscribe((corporations) => {
          if (!corporations || corporations.length === 0) {
            return;
          }
          this.corporations = corporations;
          this.selectedCorporationId =
            this.storageService.getSelectedCorporationId() ??
            corporations[0]?.id ??
            "";
          if (this.selectedCorporationId) {
            this.storageService.setSelectedCorporationId(
              this.selectedCorporationId
            );
            this.fetchFacilitiesForCorporation(this.selectedCorporationId);
          }
        });

      this.shouldReloadFacilitiesDropDownSubscription =
        this.storageService.shouldReloadFacilitiesDropDown.subscribe((res) => {
          if (res) {
            this.fetchFacilitiesForCorporation(
              this.storageService.getSelectedCorporationId() ?? ""
            );
          }
        });
      this.shouldReloadCorporationDropDownSubscription =
        this.storageService.shouldReloadCorporationDropDown.subscribe((res) => {
          if (res) {
            this.fetchCorporationsForUser();
          }
        });
    } else {
      this.fetchFacilitiesForUser(this.user);
      this.shouldReloadFacilitiesDropDownSubscription =
        this.storageService.shouldReloadFacilitiesDropDownForUser.subscribe(
          (res) => {
            if (res) {
              this.fetchFacilitiesForUser(this.user);
            }
          }
        );
    }

    this.updatePatientForSelectedFacility();
    if (!this.selectedFacilityId) {
      this.links = this.buildLinks();
    }
    this.trackRouteChanges();
  }
  ngOnDestroy() {
    this.corporationsForUserSubscription?.unsubscribe();
    this.shouldReloadFacilitiesDropDownSubscription?.unsubscribe();
    this.shouldReloadCorporationDropDownSubscription?.unsubscribe();
  }
  logout() {
    this.authService.logout();
  }
  onchangePassword(){
    window.location.href=`${environment.zpaxChangepasswordEndpoint}`
  }
  
  get facilityProviderNumber() {
    return (
      this.storageService.getFacilities().find((facility) => {
        return facility.facilityId === this.selectedFacilityId;
      })?.providerNumber ?? undefined
    );
  }

  /**
   * This method exists because the interface and the return value cached in local storage do not match.
   * This will leverage the expected properties, and fall back to those used by the local test user
   */
  private buildUserDisplayName(user: any): string {
    if (user?.firstname && user?.lastname) {
      return `${user.firstname} ${user.lastname}`;
    }
    return user?.fullname;
  }

  private trackRouteChanges(): void {
    const nonAdminRoutes = ["patient", "time-card", "agenda"];

    this.isInAdminRoute = !nonAdminRoutes.some(
      (route) => this.router.url.indexOf(route) > -1
    );
    this.router.events
      .pipe(
        filter((event: Event) => event instanceof NavigationEnd),
        map((event: Event) => event as NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe((navigationEnd: NavigationEnd) => {
        this.isInAdminRoute = !nonAdminRoutes.some(
          (route) => navigationEnd.url.indexOf(route) > -1
        );
      });
  }

  private determineInitialFacilityIdSelection(
    facilities: UserFacility[]
  ): string {
    const selectedFacilityId = this.storageService.getSelectedFacilityId();
    let initialFacilityIdSelection;
    if (facilities.length > 0) {
      if (
        selectedFacilityId &&
        facilities.find((item) => item.facilityid === selectedFacilityId)
      ) {
        initialFacilityIdSelection = selectedFacilityId;
      } else if (
        facilities.find(
          (item) => item.facilityid === this?.user?.defaultfacilityid
        )
      ) {
        initialFacilityIdSelection = this?.user?.defaultfacilityid;
      } else {
        initialFacilityIdSelection = facilities[0].facilityid;
      }
    }
    this.storageService.setSelectedFacilityId(initialFacilityIdSelection);
    this.navigationHelperService.setSelectedFacilityId(
      initialFacilityIdSelection
    );
    return initialFacilityIdSelection;
  }

  private async fetchFacilitiesForUser(user: any): Promise<void> {
    if (user?.id) {
      this.facilitiesForUser$.next(
        await firstValueFrom(
          this.userClient
            .getUserFacilities(user.id)
            .pipe(map((listResponse: UserFacility[]) => listResponse))
        )
      );
    }
  }

  async updateFacilitySelection(selectedId: string): Promise<void> {
    this.selectedFacilityId = selectedId;
    this.storageService.setSecurityFeatureData(
      await firstValueFrom(
        this.securityGroupClient.getFeaturePermission(selectedId)
      )
    );
    this.storageService.setSelectedFacilityId(this.selectedFacilityId);
    this.navigationHelperService.setSelectedFacilityId(this.selectedFacilityId);
    this.updatePatientForSelectedFacility();
    this.links = this.buildLinks();
    this.updateUserCorporationAndFacilitySelection();
  }

  private updateUserCorporationAndFacilitySelection(): void {
    if(this.user?.issuperuser){
      const corpId = this.storageService.getSelectedCorporationId();
      const facilityId = this.storageService.getSelectedFacilityId();
      if(!!corpId && !!facilityId){
        this.userClient.updateLatestCorpFacility(this.selectedCorporationId!, this.selectedFacilityId).then();
      }
    }
  }

  private buildLinks(): LinkGroupConfig[] {
    const links = [
      {
        name: "Users",
        routerLink: ["/user"],
      } as LinkConfig,
    ] as LinkConfig[];

    if (this.user.issuperuser) {
      links.unshift({
        name: "Facilities",
        routerLink: ["/facility"],
      } as LinkConfig);
    }

    if (this.securityUtility.linkAccessible(Features.CORPORATION_LIST)) {
      links.unshift({
        name: "Corporations",
        routerLink: ["/corporation"],
      } as LinkConfig);
    }

    const additionalLinks = [];

    if (this.patient) {
      additionalLinks.push({
        name: "Forecast",
        routerLink: [
          "/patient",
          this.selectedFacilityId,
          "profile",
          this.patient.id,
          "forecast",
        ],
      } as LinkConfig);
    }

    return [
      { links } as LinkGroupConfig,
      {
        links: [
          {
            name: "Agenda",
            routerLink: ["/agenda"],
          } as LinkConfig,
          ...additionalLinks,
          {
            name: "Patients",
            routerLink: ["/patient", this.selectedFacilityId],
          } as LinkConfig,
          {
            name: "Timecard",
            routerLink: ["/time-card"],
          } as LinkConfig,
        ],
      } as LinkGroupConfig,
    ];
  }

  private getDefaultFacilityId(facilities: UserFacility[]) {
    const defaultFacility = facilities.find(
      (facility) => facility.isdefaultfacility
    );
    this.defaultFacilityId = defaultFacility?.facilityid || "";
  }

  private updatePatientForSelectedFacility() {
    this.facilitiesForUser$?.subscribe((facilities) => {
      this.facilities = facilities;
      if (facilities?.length > 0) {
        this.selectedFacilityId =
          this.determineInitialFacilityIdSelection(facilities);
        this.navigationHelperService.setSelectedFacilityId(
          this.selectedFacilityId
        );
        this.getDefaultFacilityId(facilities);
        this.patientClient
          .getPatientsForecastList({
            facilityId: this.selectedFacilityId,
            page: 0,
            length: 1,
            sortBy: "name",
            sortDir: "asc",
          })
          .pipe(first())
          .subscribe((response) => {
            this.patient = response.data[0];
            this.navigationHelperService.setSelectedPatient(this.patient);
            this.links = this.buildLinks();
          });
      }
    });
  }

  private fetchCorporationsForUser(): void {
    this.corporationClient
      .getCorporationsDropdown()
      .pipe(
        map((response) => response || []),
        untilDestroyed(this)
      )
      .subscribe((corpList) => this.corporationsForUser$.next(corpList));
  }

  onCorporationSelected(event: MatSelectChange): void {
    this.selectedCorporationId = event.value;
    this.storageService.setSelectedCorporationId(this.selectedCorporationId!);
    this.fetchFacilitiesForCorporation(this.selectedCorporationId!, true);
    this.updateUserCorporationAndFacilitySelection();
  }

  private async fetchFacilitiesForCorporation(corpId: string, isheaderdrop: boolean = false): Promise<void> {
    this.facilitiesForUser$.next(
      await firstValueFrom(
        this.facilitiesClient.getFacilitiesDropdown(corpId).pipe(
          map((dropdownItems: DropdownItem[]) =>
            dropdownItems.map((item) => ({
              userid: "",
              corpid: corpId,
              facilityid: item.id || "",
              securitygroupid: "",
              isdefaultfacility: true,
              customizesecurityfeature: true,
              facilityname: item.name,
              levelid: 0,
              depth: 0,
              levelname: "",
              parentlevelid: 0,
              parentlevelname: "",
              parentofparentlevelname: "",
              corporation: corpId,
              securitygroupname: "",
            }))
          )
        )
      )
    );

    this.facilitiesForUser$.pipe(take(1)).subscribe((facilities) => {
      this.facilities = facilities;
      const selectedFacilityId = this.storageService.getSelectedFacilityId();
      if (facilities.length > 0) {
        if (
          selectedFacilityId &&
          facilities.find((item) => item.facilityid === selectedFacilityId)
        ) {
          this.updateFacilitySelection(selectedFacilityId);
          this.updatePatientForSelectedFacility();
        } else {
          this.updateFacilitySelection(facilities[0].facilityid);
          this.updatePatientForSelectedFacility();
        }
      } else if (isheaderdrop) {
        this.matDialog
          .open(ConfirmationModalComponent, {
            data: {
              title: "No Facility Available",
              message: "No facility assigned to the selected corporation.",
              buttonText: "OK",
              hideCancelButton: true,
              isError: true,
            },
          })
          .afterClosed()
          .subscribe(() => {
            this.storageService.setSelectedFacilityId("");
            this.navigationHelperService.setSelectedPatient(null);
            this.navigationHelperService.setSelectedFacilityId("");
            this.router.navigate(["home"]);
          });
      } else {
        this.storageService.setSelectedFacilityId("");
        this.navigationHelperService.setSelectedPatient(null);
        this.navigationHelperService.setSelectedFacilityId("");
      }
    });
  }
}
